import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, formatNaira } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useBasket } from "@/context/BasketContext";
import { toast } from "sonner";
import { ProductCard } from "@/components/ProductCard";
import { Phone, MessageCircle, ArrowLeft, Loader2, CheckCircle2, XCircle, Plus } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { settings } = useStore();
  const { add } = useBasket();

  const { data: product, isLoading, isError } = useQuery({
    queryKey: ["product", slug],
    queryFn: () => api.get(`/product/${slug}`).catch(() => api.get(`/products/${slug}`)).then((r) => r.data),
  });

  const { data: related = [] } = useQuery({
    queryKey: ["related", product?.category_slug],
    queryFn: () => api.get("/products", { params: { category: product.category_slug, limit: 5 } }).then((r) => r.data),
    enabled: !!product?.category_slug,
  });

  if (isLoading) {
    return <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand-blue" /></div>;
  }
  if (isError || !product) {
    return (
      <div className="py-32 text-center">
        <p className="font-display font-bold text-2xl text-brand-blue">Product not found</p>
        <Link to="/shop" className="mt-4 inline-block text-brand-blue underline">Back to shop</Link>
      </div>
    );
  }

  const whatsappNumber = (settings?.whatsapp || "").replace(/[^0-9]/g, "");
  const orderMsg = encodeURIComponent(`Hello BooksNmore, I'd like to order: ${product.name} (${formatNaira(product.price)}).`);
  const inStock = product.stock > 0;
  const relatedFiltered = related.filter((r) => r.slug !== product.slug).slice(0, 4);

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
      <button onClick={() => navigate(-1)} data-testid="back-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-brand-blue hover:text-brand-gold mb-6">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-2 gap-10">
        <div className="border-2 border-black rounded-lg overflow-hidden shadow-brutal-lg bg-brand-cream">
          {product.images?.[0] ? (
            <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover aspect-square" />
          ) : (
            <div className="aspect-square flex items-center justify-center text-brand-blue/40 font-display font-bold">No image</div>
          )}
        </div>

        <div>
          <Link to={`/shop?category=${product.category_slug}`} className="inline-block bg-brand-gold text-black text-xs font-bold px-3 py-1 border-2 border-black rounded-md mb-4 capitalize" data-testid="product-category-badge">
            {product.category_slug.replace(/-/g, " ")}
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-brand-blue leading-tight">{product.name}</h1>
          <p className="mt-4 text-3xl font-black text-brand-black" data-testid="product-price">{formatNaira(product.price)}</p>
          {product.unit && <p className="mt-1 text-sm font-semibold text-brand-blue/70" data-testid="product-unit">Sold as: {product.unit}</p>}

          <div className="mt-3 flex items-center gap-2 text-sm font-semibold" data-testid="product-stock">
            {inStock ? (
              <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="w-4 h-4" /> In stock</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-red-600"><XCircle className="w-4 h-4" /> Currently out of stock</span>
            )}
            {product.brand && <span className="text-brand-black/50">· {product.brand}</span>}
            {product.level && <span className="text-brand-black/50 capitalize">· {product.level}</span>}
          </div>

          {product.description && (
            <p className="mt-6 text-brand-black/70 leading-relaxed">{product.description}</p>
          )}

          <div className="mt-8 p-5 bg-brand-cream border-2 border-black rounded-lg">
            <button
              onClick={() => { add(product); toast.success(`${product.name} added to your list`, { action: { label: "View list", onClick: () => navigate("/basket") } }); }}
              data-testid="detail-add-to-list"
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-gold text-black font-semibold px-5 py-3 border-2 border-black rounded-md shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 transition-all mb-4"
            >
              <Plus className="w-5 h-5" strokeWidth={2.5} /> Add to my shopping list
            </button>
            <p className="font-display font-bold text-brand-blue mb-1">Or place a telephone order</p>
            <p className="text-sm text-brand-black/60 mb-4">Contact us to buy this item — we'll confirm price, payment & delivery.</p>
            <div className="flex flex-wrap gap-3">
              {settings?.phone && (
                <a href={`tel:${settings.phone}`} data-testid="product-call-btn" className="inline-flex items-center gap-2 bg-brand-blue text-white font-semibold px-5 py-3 border-2 border-black rounded-md shadow-brutal hover:-translate-y-0.5 transition-all">
                  <Phone className="w-5 h-5" /> Call to order
                </a>
              )}
              {whatsappNumber && (
                <a href={`https://wa.me/${whatsappNumber}?text=${orderMsg}`} target="_blank" rel="noopener noreferrer" data-testid="product-whatsapp-btn" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-5 py-3 border-2 border-black rounded-md shadow-brutal-black hover:-translate-y-0.5 transition-all">
                  <MessageCircle className="w-5 h-5" /> Order on WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {relatedFiltered.length > 0 && (
        <div className="mt-20">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-blue mb-6">You might also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {relatedFiltered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
